import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { PropsWithChildren } from 'react';

type ThemeId = 'auto' | 'charcoal' | 'midnight' | 'dawn';
type ResolvedThemeId = Exclude<ThemeId, 'auto'>;

type ThemeOption = {
  id: ThemeId;
  label: string;
};

type ThemeContextValue = {
  themeId: ThemeId;
  resolvedThemeId: ResolvedThemeId;
  availableThemes: ThemeOption[];
  setTheme: (id: ThemeId) => void;
};

const THEME_STORAGE_KEY = 'dashboard:theme-selection';

const THEME_OPTIONS: ThemeOption[] = [
  { id: 'auto', label: 'System' },
  { id: 'charcoal', label: 'Charcoal' },
  { id: 'midnight', label: 'Midnight' },
  { id: 'dawn', label: 'Dawn' },
];

function getSystemTheme(): ResolvedThemeId {
  if (typeof window === 'undefined') {
    return 'charcoal';
  }
  const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches;
  return prefersDark ? 'charcoal' : 'dawn';
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export function ThemeProvider({ children }: PropsWithChildren) {
  const [systemTheme, setSystemTheme] = useState<ResolvedThemeId>(() => getSystemTheme());

  const [themeId, setThemeId] = useState<ThemeId>(() => {
    if (typeof window === 'undefined') {
      return 'charcoal';
    }
    try {
      const stored = window.localStorage.getItem(THEME_STORAGE_KEY) as ThemeId | null;
      if (!stored) {
        return 'charcoal';
      }
      return THEME_OPTIONS.some((option) => option.id === stored) ? stored : 'charcoal';
    } catch {
      return 'charcoal';
    }
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (event: MediaQueryListEvent) => {
      setSystemTheme(event.matches ? 'charcoal' : 'dawn');
    };

    try {
      mediaQuery.addEventListener('change', handleChange);
    } catch {
      // Safari fallback
      mediaQuery.addListener(handleChange);
    }

    return () => {
      try {
        mediaQuery.removeEventListener('change', handleChange);
      } catch {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  const resolvedThemeId: ResolvedThemeId = useMemo(() => {
    return themeId === 'auto' ? systemTheme : themeId;
  }, [themeId, systemTheme]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, themeId);
    } catch {
      // ignore storage failures
    }
  }, [themeId]);

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', resolvedThemeId);
  }, [resolvedThemeId]);

  const setTheme = useCallback((id: ThemeId) => {
    setThemeId(id);
  }, []);

  const contextValue: ThemeContextValue = useMemo(
    () => ({
      themeId,
      resolvedThemeId,
      availableThemes: THEME_OPTIONS,
      setTheme,
    }),
    [resolvedThemeId, setTheme, themeId],
  );

  return <ThemeContext.Provider value={contextValue}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return ctx;
}
